import { useMemo, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import {
  eur, formatSeasons, pick, startingPrice, useContent,
} from "@/lib/content";

/**
 * The rates section — the reason most visitors are here ("Tarife, ce aveți
 * disponibil? Cât costă?"). Every card leads with a number; the full grid is
 * one click away rather than dumped on the page.
 */

const GROUP_ORDER = ["migratory", "small_game", "big_game"];

const PriceTable = ({ species, t, lang }) => {
  const p = species.pricing;

  if (p.kind === "on_request") {
    return <p className="font-body text-sm text-stone">{t.pricing.onRequest}</p>;
  }

  if (p.kind === "flat") {
    return (
      <p className="font-body text-sm text-ink">
        <span className="font-display text-2xl">{eur(p.price, lang)}</span>
        {pick(p.label, lang) && (
          <span className="ml-2 text-stone">— {pick(p.label, lang)}</span>
        )}
      </p>
    );
  }

  if (p.kind === "group_tiers" || p.kind === "bag_tiers") {
    return (
      <table className="w-full font-body text-sm">
        <tbody>
          {p.rows.map((row, i) => (
            <tr key={i} className="border-b border-hairline/60 last:border-0">
              <td className="py-2 pr-4 text-stone">{pick(row.label, lang)}</td>
              <td className="py-2 text-right font-medium text-ink">
                {row.onRequest
                  ? t.pricing.onRequest
                  : <>{eur(row.price, lang)} <span className="text-xs text-stone">{t.pricing.perHunter}</span></>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // trophy_grid
  return (
    <div className="flex flex-col gap-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-olive">
        {t.pricing.trophyFee}
      </span>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[320px] font-body text-sm">
          <tbody>
            {p.rows.map((row, i) => (
              <tr key={i} className="border-b border-hairline/60 last:border-0">
                <td className="py-2 pr-4 text-stone">{pick(row.range, lang)}</td>
                <td className="py-2 text-right font-medium text-ink">
                  {eur(row.price, lang)} <span className="text-xs text-stone">{t.pricing.perPiece}</span>
                </td>
                <td className="py-2 pl-4 text-right text-xs text-stone">
                  {row.adder ? `+ ${eur(row.adder.eur, lang)} / ${row.adder.per}` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {p.cull && (
        <p className="font-body text-xs text-stone">
          <span className="text-olive">{t.pricing.cull}:</span> {eur(p.cull.price, lang)}
          {pick(p.cull.note, lang) && ` — ${pick(p.cull.note, lang)}`}
        </p>
      )}
      {typeof p.woundedPct === "number" && (
        <p className="font-body text-xs text-stone">{t.pricing.wounded(p.woundedPct)}</p>
      )}
      {typeof p.woundedFlat === "number" && (
        <p className="font-body text-xs text-stone">{t.pricing.woundedFlat(p.woundedFlat)}</p>
      )}
      {typeof p.missedShot === "number" && (
        <p className="font-body text-xs text-stone">{t.pricing.missedShot(p.missedShot)}</p>
      )}
    </div>
  );
};

const SpeciesCard = ({ species, t, lang }) => {
  const [open, setOpen] = useState(false);
  const from = startingPrice(species);
  const season = formatSeasons(species.seasons, lang, t);
  const pkg = species.package || {};

  return (
    <article
      data-testid={`species-card-${species.slug}`}
      className="flex flex-col border border-hairline bg-bone transition-colors duration-500 hover:border-olive/50"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex flex-col items-start gap-3 px-6 py-6 text-left md:px-8"
      >
        <div className="flex w-full items-baseline justify-between gap-4">
          <h3 className="font-display text-2xl font-medium uppercase tracking-tight text-ink md:text-3xl">
            {pick(species.names, lang)}
          </h3>
          <span className="whitespace-nowrap text-right">
            {from ? (
              <>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone">
                  {t.pricing.from}{" "}
                </span>
                <span className="font-display text-2xl text-ink md:text-3xl">
                  {eur(from, lang)}
                </span>
              </>
            ) : (
              <span className="font-body text-sm uppercase tracking-wide text-rust">
                {t.pricing.onRequest}
              </span>
            )}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-[0.15em] text-stone">
          {season && <span>{season}</span>}
          {pkg.days && <span>· {t.pricing.days(pkg.days)}</span>}
          {pkg.groupSize && <span>· {t.pricing.group} {pkg.groupSize}</span>}
          {species.dynamic?.enabled && (
            <span className="text-olive">· {t.pricing.configure}</span>
          )}
        </div>
      </button>

      {open && (
        <div className="flex flex-col gap-5 border-t border-hairline px-6 py-6 md:px-8">
          {pkg.basePrice && (
            <p className="font-body text-sm text-stone">
              {t.pricing.packageFrom}{" "}
              <span className="font-medium text-ink">{eur(pkg.basePrice, lang)}</span>
              {" "}{t.pricing.perHunter}
              {pkg.days ? ` — ${t.pricing.days(pkg.days)}` : ""}
              {pkg.outings ? `, ${t.pricing.outings(pkg.outings)}` : ""}
            </p>
          )}

          <PriceTable species={species} t={t} lang={lang} />

          {pkg.includes?.length > 0 && (
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-olive">
                {t.pricing.includes}
              </span>
              <ul className="mt-2 flex flex-col gap-1 font-body text-sm text-stone">
                {pkg.includes.map((key) => (
                  <li key={key}>— {t.services[key] || key}</li>
                ))}
              </ul>
            </div>
          )}

          {pick(pkg.notes, lang) && (
            <p className="font-body text-sm italic leading-relaxed text-stone">
              {pick(pkg.notes, lang)}
            </p>
          )}

          <a
            href="#cerere"
            className="self-start border-b border-ink pb-0.5 font-body text-[13px] font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:border-rust hover:text-rust"
          >
            {t.pricing.cta} →
          </a>
        </div>
      )}
    </article>
  );
};

export const Pricing = () => {
  const { t, lang } = useLang();
  const { data, isLoading } = useContent();

  const grouped = useMemo(() => {
    const species = data?.species ?? [];
    return GROUP_ORDER
      .map((key) => ({ key, items: species.filter((s) => s.grp === key) }))
      .filter((g) => g.items.length > 0);
  }, [data]);

  return (
    <section id="tarife" data-testid="pricing-section" className="bg-sand">
      <div className="mx-auto max-w-[1200px] px-5 py-24 md:px-10 md:py-32">
        <Reveal>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-rust">
            {t.pricing.overline}
          </span>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 font-display text-5xl font-medium uppercase leading-[0.9] tracking-tight text-ink md:text-7xl">
            {t.pricing.title}
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-stone">
            {t.pricing.body}
          </p>
        </Reveal>

        {isLoading && (
          <div className="mt-12 flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 animate-pulse border border-hairline bg-bone/60" />
            ))}
          </div>
        )}

        {!isLoading && grouped.length === 0 && (
          <p className="mt-12 border border-dashed border-hairline p-10 text-center font-body text-stone">
            {t.pricing.empty}
          </p>
        )}

        {grouped.map((group) => (
          <div key={group.key} className="mt-14">
            <Reveal>
              <h3 className="mb-5 font-mono text-[11px] uppercase tracking-[0.3em] text-olive">
                {t.pricing.groups[group.key]}
              </h3>
            </Reveal>
            <div className="flex flex-col gap-3">
              {group.items.map((s, i) => (
                <Reveal key={s.id} delay={Math.min(i * 0.05, 0.3)}>
                  <SpeciesCard species={s} t={t} lang={lang} />
                </Reveal>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
